import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import axiosInstance from 'services/axiosInstance';

type PodRequestConfig = AxiosRequestConfig & {
  skipErrorSnackbar?: boolean;
  rejectWithResponse?: boolean;
};

const podRequest = (config: PodRequestConfig): AxiosRequestConfig => config;

export interface ICreateSchedulingRequestPayload {
  appointmentTypeId: number;
  psaTicketId?: string;
  psaCrmDefaultId?: number;
  contactEmail: string;
  contactName: string;
  contactPhone?: string;
  assignedTechUserId?: number;
  assignedTechUserIds?: number[];
  channels?: string[];
  durationOverrideMinutes?: number;
  advanceNoticeHoursOverride?: number;
  includeIntakeQuestions?: boolean;
  guests?: ISchedulingRequestGuestPayload[];
  bookingWindowStartDate?: string;
  bookingWindowEndDate?: string;
  dailyStartTime?: string;
  dailyEndTime?: string;
  assignmentStrategy?: number;
  distributionMode?: number;
  technicianGroupId?: number;
  allowedMeetingProviders?: number[];
}

/** An extra attendee pre-selected on the link; email-only notifications, no channel of their own. */
export interface ISchedulingRequestGuestPayload {
  name?: string;
  email: string;
  psaContactId?: string;
}

/** A custom intake question configured on an appointment type. */
export interface IAppointmentTypeQuestion {
  id: number;
  label: string;
  inputType: string;
  options?: string[] | null;
  isRequired: boolean;
  sortOrder: number;
}

export interface ITechnicianSummary {
  userId: number;
  name: string;
  email: string;
  psaWriteStatus?: IPsaWriteStatus | null;
  meetingProviders?: number[];
}

/**
 * Outcome of writing this booking to the technician's PSA calendar. Null when no PSA write was
 * attempted. Anonymous booking routes never carry it — the provider message is raw partner text.
 */
export interface IPsaWriteStatus {
  provider: number;
  status: number;
  message: string | null;
  detail?: string | null;
  kind?: PsaFailureKind | null;
  attemptCount: number;
  lastAttemptUtc: string | null;
  externalId: string | null;
}

/**
 * What failed, named by the API rather than recovered from its wording. Carried as a string on the
 * wire deliberately, so that no global serializer setting can turn it into an integer.
 */
export type PsaFailureKind = 'psa-entry' | 'calendar-entry' | 'meeting-link';

/**
 * A calendar or meeting-link failure as structured data. The API also sends calendarWriteErrors,
 * the same failures as English sentences; this array supersedes it where present, and the two are
 * carried together so the pod and the API can deploy in either order.
 */
export interface ICalendarWriteFailure {
  kind: PsaFailureKind;
  provider: number;
  techUserId: number | null;
  technicianName: string | null;
  message: string | null;
  detail?: string | null;
  retryable: boolean;
}

/** The API serializes enums as integers — no StringEnumConverter is registered. */
export const PSA_WRITE_PENDING = 0;
export const PSA_WRITE_SUCCEEDED = 1;
export const PSA_WRITE_FAILED = 2;

/** CalendarProviderType. psaWriteStatus only ever carries the PSA block (10+). */
export const CALENDAR_PROVIDER_NAMES: Record<number, string> = {
  1: 'Microsoft 365',
  2: 'Google',
  3: 'Zoom',
  10: 'ConnectWise',
  11: 'Autotask',
  12: 'Halo',
};

/** What POST /SchedulingPod/book-direct returns — BookingModel, as the raw 200 body. */
export interface IBookingResult {
  id: number;
  /** Who the appointment was actually assigned to, which round robin can decide after the pod has drawn. */
  technicianName?: string | null;
  calendarWriteErrors?: string[] | null;
  calendarWriteFailures?: ICalendarWriteFailure[] | null;
  technicians?: ITechnicianSummary[];
}

/** Per-channel outcome of a booking-link send. success = the delivery service accepted it. */
export interface INotificationDispatchResult {
  channel: string;
  success: boolean;
  message: string | null;
}

export interface ISchedulingRequest {
  id: number;
  appointmentTypeId: number;
  appointmentTypeName: string;
  psaTicketId: string;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  token: string;
  bookingUrl: string | null;
  shareUrl?: string | null;
  sentAt: string;
  expiresAt: string | null;
  status: number;
  reminderSentAt: string | null;
  assignedTechUserId: number | null;
  guestCount?: number;
  technicians: ITechnicianSummary[];
  createdAt: string;
  bookedStartDateTimeUtc: string | null;
  bookedTimezone: string | null;
  bookingId?: number | null;
  calendarWriteErrors?: string[] | null;
  calendarWriteFailures?: ICalendarWriteFailure[] | null;
  durationOverrideMinutes?: number | null;
  advanceNoticeHoursOverride?: number | null;
  bookingWindowStartDate?: string | null;
  bookingWindowEndDate?: string | null;
  dailyStartTime?: string | null;
  dailyEndTime?: string | null;
  notificationResults?: INotificationDispatchResult[];
  reusedExistingRequest?: boolean;
  sendFailed?: boolean;
  failedChannels?: string | null;
  sendFailureReason?: string | null;
}

export interface IAppointmentType {
  id: number;
  publicSlug: string;
  name: string;
  isGlobal: boolean;
  description: string;
  durationOptions: number[];
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  advanceNoticeHours: number;
  locationType: number;
  assignmentStrategy: number;
  distributionMode: number;
  reminderOffsetHours: number;
  expiryTimeoutHours: number;
  isActive: boolean;
  bookingPageUrl?: string;
  maxBookingHorizonDays?: number | null;
}

export interface ICalendarReadiness {
  hasGlobalAccount: boolean;
  currentUserHasCalendar: boolean;
  hasMeetingProvider: boolean;
  meetingProviders?: number[];
  isReady: boolean;
  reason: string | null;
  tenantTimezone?: string | null;
}

export const ACCESS_CAUSE = {
  moduleDisabled: 'ModuleDisabled',
  accountDisabled: 'AccountDisabled',
  notReady: 'NotReady',
  userNotFound: 'UserNotFound',
} as const;

export interface IScheduleAssistAccess {
  hasAccess: boolean;
  reason: string | null;
  cause: string | null;
  /**
   * Whether the calling user may book directly from the pod. Absent on an
   * older API — treat that as true rather than hiding the tab for a server
   * that has not shipped the field yet. Meaningless on a denial response
   * (`hasAccess: false`): read it only when `hasAccess` is true, since the
   * denial branches return early with their own model and this defaults to
   * false without saying anything about the tenant's actual setting.
   */
  bookNowEnabled?: boolean;
  meetingFormatLabels?: {
    remote?: string | null;
    onSite?: string | null;
    phone?: string | null;
  } | null;
}

export const DEFAULT_MEETING_FORMAT_LABELS: Record<number, string> = {
  0: 'Remote',
  1: 'On-site',
  2: 'Phone',
};

const withFallback = (value: string | null | undefined, fallback: string): string => {
  const trimmed = value?.trim();
  if (trimmed) {
    return trimmed;
  }
  return fallback;
};

export const resolveMeetingFormatLabels = (
  access?: IScheduleAssistAccess | null,
): Record<number, string> => {
  const labels = access?.meetingFormatLabels;
  return {
    0: withFallback(labels?.remote, DEFAULT_MEETING_FORMAT_LABELS[0]),
    1: withFallback(labels?.onSite, DEFAULT_MEETING_FORMAT_LABELS[1]),
    2: withFallback(labels?.phone, DEFAULT_MEETING_FORMAT_LABELS[2]),
  };
};

export interface IPodSetupState {
  moduleEnabled: boolean;
  access: IScheduleAssistAccess | null;
  readiness: ICalendarReadiness | null;
  appointmentTypes: IAppointmentType[] | null;
}

export interface IAvailableSlot {
  startDateTimeUtc: string;
  endDateTimeUtc: string;
  durationMinutes: number;
}

export interface IDirectBookPayload {
  startDateTimeUtc: string;
  durationMinutes: number;
  clientTimezone: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
}

export interface IPodDirectBookPayload {
  appointmentTypeSlug: string;
  startDateTimeUtc: string;
  durationMinutes: number;
  clientTimezone: string;
  contactName: string;
  contactEmail: string;
  contactPhone?: string;
  psaTicketId?: string;
  psaCrmDefaultId?: number;
  techUserIds?: number[];
  assignmentStrategy?: number;
  distributionMode?: number;
  technicianGroupId?: number;
  meetingProvider?: number;
}

export interface ICalendarEventAttendee {
  name: string | null;
  email: string | null;
  responseStatus: string | null;
}

export interface ICalendarEventDetail {
  id: string;
  externalEventId: string | null;
  connectionId: number;
  userId: number;
  userName: string | null;
  userColor: string | null;
  provider: number;
  providerName: string | null;
  subject: string | null;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  location: string | null;
  description: string | null;
  organizer: ICalendarEventAttendee | null;
  attendees: ICalendarEventAttendee[] | null;
  meetingLink: string | null;
  providerColor: string | null;
  webLink: string | null;
  linkedTicketId: string | null;
}

export interface ICalendarConnectionInfo {
  id: number;
  userId: number;
  userName: string | null;
  provider: number;
  providerName: string | null;
  label: string | null;
}

export interface ICalendarEventError {
  connectionId: number;
  userId: number;
  userName: string | null;
  provider: number;
  message: string | null;
}

export interface IAdminTechnician {
  userId: number;
  name: string | null;
  email: string | null;
  isScheduleAssistReady: boolean;
  isScheduleAssistDisabled?: boolean;
  color?: string | null;
}

export interface ICalendarGroup {
  id: number;
  name: string;
  techUserIds: number[];
  hiddenConnectionIds: string[];
  hiddenUserIds: number[];
}

export interface ICalendarGroupPayload {
  name?: string;
  techUserIds?: number[];
  hiddenConnectionIds?: string[];
  hiddenUserIds?: number[];
}

export interface ITechnicianGroup {
  id: number;
  name: string;
  sortOrder: number;
  memberUserIds: number[];
  memberCount: number;
  distributionMode?: number;
}

export interface IUnifiedCalendarResponse {
  events: ICalendarEventDetail[];
  connections: ICalendarConnectionInfo[];
  errors: ICalendarEventError[];
}

export const CALENDAR_UNAVAILABLE = 'CalendarUnavailable';

export type CalendarIssueProvider = 'Microsoft365' | 'Google' | 'ConnectWise' | 'Autotask' | 'Halo';

export type CalendarFixAction = 'ReconnectCalendar' | 'OrganizationSettings' | 'PsaSettings' | 'Retry';

export interface ICalendarIssue {
  technicianUserId: number;
  technicianName: string;
  provider: CalendarIssueProvider;
  accountEmail: string | null;
  reasonCode: string;
  reason: string;
  fixAction: CalendarFixAction;
  failingSince: string;
  lastCheckedAt: string;
}

export interface ICalendarHealthCheck {
  blocking: boolean;
  degraded: boolean;
  audience: 'admin' | 'technician';
  message: string | null;
  issues: ICalendarIssue[];
  excludedCount: number;
  candidateCount: number;
  checkedAt: string;
}

export const parseCalendarHealth = (body: unknown): ICalendarHealthCheck | null => {
  const read = (value: unknown): Record<string, unknown> | null =>
    value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  const root = read(body);
  const candidate = [root, read(root?.result), read(root?.data)].find(
    c => c !== null && typeof c.blocking === 'boolean',
  );
  if (!candidate) return null;
  const audience = candidate.audience === 'admin' ? 'admin' : 'technician';
  return {
    blocking: candidate.blocking === true,
    degraded: candidate.degraded === true,
    audience,
    message: typeof candidate.message === 'string' ? candidate.message : null,
    issues: audience === 'admin' && Array.isArray(candidate.issues) ? (candidate.issues as ICalendarIssue[]) : [],
    excludedCount: typeof candidate.excludedCount === 'number' ? candidate.excludedCount : 0,
    candidateCount: typeof candidate.candidateCount === 'number' ? candidate.candidateCount : 0,
    checkedAt: typeof candidate.checkedAt === 'string' ? candidate.checkedAt : '',
  };
};

const APPOINTMENT_TYPES_URL = '/ScheduleAssist/AppointmentTypes';
const APPOINTMENT_TYPES_PAGE_SIZE = 500;

const APPOINTMENT_TYPES_MAX_PAGES = 20;
const CALENDAR_EVENTS_URL = '/ScheduleAssist/CalendarEvents';
const ADMIN_TECHNICIANS_URL = '/ScheduleAssist/Admin/Technicians';
const CALENDAR_GROUPS_URL = '/ScheduleAssist/CalendarGroups';
const TECHNICIAN_GROUPS_URL = '/SchedulingPod/technician-groups';

/**
 * Reads every appointment type, not the server's default first page. Requests a bounded page and
 * keeps going while a full page comes back, so the list cannot be silently truncated at any size.
 * Returns the last response with the accumulated rows so callers keep their status handling.
 */
const fetchAppointmentTypesPaged = async (
  config: Record<string, unknown> = {},
): Promise<AxiosResponse<IAppointmentType[]>> => {
  const rows: IAppointmentType[] = [];
  let response: AxiosResponse<IAppointmentType[]> | undefined;
  for (let page = 0; page < APPOINTMENT_TYPES_MAX_PAGES; page += 1) {
    const start = page * APPOINTMENT_TYPES_PAGE_SIZE;
    // eslint-disable-next-line no-await-in-loop
    response = await axiosInstance.get<IAppointmentType[]>(APPOINTMENT_TYPES_URL, {
      ...config,
      params: { _start: start, _end: start + APPOINTMENT_TYPES_PAGE_SIZE },
    });
    const pageRows = Array.isArray(response.data) ? response.data : [];
    rows.push(...pageRows);
    if (pageRows.length < APPOINTMENT_TYPES_PAGE_SIZE) {
      break;
    }
  }
  return { ...(response as AxiosResponse<IAppointmentType[]>), data: rows };
};

const scheduleAssistService = {
  getAppointmentTypes: () => fetchAppointmentTypesPaged(),

  /** Intake questions configured on an appointment type. Silent: an empty list is a valid answer. */
  getAppointmentTypeQuestions: (appointmentTypeId: number) =>
    axiosInstance.request<IAppointmentTypeQuestion[]>({
      method: 'GET',
      url: `${APPOINTMENT_TYPES_URL}/${appointmentTypeId}/Questions`,
      validateStatus: () => true,
    }),

  getRequestsByTicketId: (ticketId: string) =>
    axiosInstance.request<ISchedulingRequest[]>({
      method: 'GET',
      url: `/SchedulingPod/requests/by-ticket/${encodeURIComponent(ticketId)}`,
      validateStatus: () => true,
    }),

  createRequest: (data: ICreateSchedulingRequestPayload) =>
    axiosInstance.request<ISchedulingRequest>(
      podRequest({
        method: 'POST',
        url: '/SchedulingPod/requests',
        data,
        // The send form surfaces a refusal itself: the eligibility guard returns a stable code that
        // the pod renders its own copy for, and a rejected create must not also flash the
        // "created, delivery unconfirmed" banner.
        rejectWithResponse: true,
      }),
    ),

  cancelRequest: (id: number) =>
    axiosInstance.request({
      method: 'POST',
      url: `/SchedulingPod/requests/${id}/cancel`,
    }),

  resendRequest: (id: number) =>
    axiosInstance.request({
      method: 'POST',
      url: `/SchedulingPod/requests/${id}/resend`,
    }),

  /** Fetches available time slots for a public appointment type by slug. */
  getAvailabilityBySlug: (slug: string, startDate: string, endDate: string, durationMinutes: number) =>
    axiosInstance.request<IAvailableSlot[]>({
      method: 'GET',
      url: `/Booking/type/${encodeURIComponent(slug)}/availability`,
      params: { startDate, endDate, durationMinutes },
    }),

  /** Books a slot directly using the public appointment type slug (public endpoint). */
  directBookBySlug: (slug: string, data: IDirectBookPayload) =>
    axiosInstance.request({
      method: 'POST',
      url: `/Booking/type/${encodeURIComponent(slug)}/book`,
      data,
    }),

  /** Books a slot directly from the Pod (authenticated endpoint with PSA context). */
  podDirectBook: (data: IPodDirectBookPayload) =>
    axiosInstance.request<IBookingResult>({
      method: 'POST',
      url: '/SchedulingPod/book-direct',
      data,
    }),

  /** Re-attempts the PSA calendar write for one technician on a booking. Returns the updated status. */
  retryPsaWrite: (bookingId: number, techUserId: number) =>
    axiosInstance.request<IPsaWriteStatus>({
      method: 'POST',
      url: `/ScheduleAssist/bookings/${bookingId}/psa-writes/${techUserId}/retry`,
    }),

  /** Aggregated events from every connected calendar provider for a UTC range. One userId sends `userId=`, several send `userIds=`, none sends neither so the backend answers with the caller's own calendar. */
  getCalendarEvents: (startUtc: string, endUtc: string, userIds?: number[]) => {
    const params = new URLSearchParams({ startUtc, endUtc });
    if (userIds && userIds.length === 1) {
      params.set('userId', String(userIds[0]));
    } else if (userIds && userIds.length > 1) {
      params.set('userIds', userIds.join(','));
    }
    return axiosInstance.request<IUnifiedCalendarResponse>({
      method: 'GET',
      url: `${CALENDAR_EVENTS_URL}?${params.toString()}`,
      validateStatus: () => true,
    });
  },

  /**
   * Every technician on the tenant with Schedule-Assist readiness metadata — the list the config
   * portal's calendar picker uses. `/SchedulingPod/technicians` is Ready AND calendar-connected
   * only, which collapses to a single person on most tenants. Admin-gated; the picker that calls
   * this only renders for roleId 1 or 2.
   */
  getAdminTechnicians: () =>
    axiosInstance.request<IAdminTechnician[]>({
      method: 'GET',
      url: ADMIN_TECHNICIANS_URL,
      validateStatus: () => true,
    }),

  /**
   * Tenant-wide technician groups for the one-click roster chips. Read through the Pod alias, not
   * the admin route, because ordinary technicians use this screen. `validateStatus` keeps a 403 or
   * 404 from throwing so the chip row simply stays hidden on tenants that cannot serve it.
   */
  getTechnicianGroups: () =>
    axiosInstance.request<ITechnicianGroup[]>({
      method: 'GET',
      url: TECHNICIAN_GROUPS_URL,
      validateStatus: () => true,
    }),

  /** Saved calendar views (per-user presets). */
  getCalendarGroups: () =>
    axiosInstance.request<ICalendarGroup[]>({
      method: 'GET',
      url: CALENDAR_GROUPS_URL,
      validateStatus: () => true,
    }),

  createCalendarGroup: (payload: ICalendarGroupPayload) =>
    axiosInstance.request<ICalendarGroup>({
      method: 'POST',
      url: CALENDAR_GROUPS_URL,
      data: payload,
      validateStatus: () => true,
    }),

  updateCalendarGroup: (id: number, payload: ICalendarGroupPayload) =>
    axiosInstance.request<ICalendarGroup>({
      method: 'PUT',
      url: `${CALENDAR_GROUPS_URL}/${id}`,
      data: payload,
      validateStatus: () => true,
    }),

  deleteCalendarGroup: (id: number) =>
    axiosInstance.request<unknown>({
      method: 'DELETE',
      url: `${CALENDAR_GROUPS_URL}/${id}`,
      validateStatus: () => true,
    }),

  /**
   * Fetches technicians with connected calendar connections for multi-tech selection. Given an
   * appointment type, the server narrows them to the ones that type offers, so the picker cannot
   * offer a technician the type would never assign.
   */
  getTechnicians: (appointmentTypeId?: number) =>
    axiosInstance.request<ITechnicianSummary[]>({
      method: 'GET',
      url: '/SchedulingPod/technicians',
      params: appointmentTypeId ? { appointmentTypeId } : undefined,
      validateStatus: () => true,
    }),

  /** Returns whether the current user may use the Pod Schedule Assist tab. */
  getMyAccess: () =>
    axiosInstance.request<IScheduleAssistAccess>({
      method: 'GET',
      url: '/SchedulingPod/access',
    }),

  /** Silent setup diagnostics — never rejects, so the global error snackbar can't fire. */
  getPodSetupState: async (): Promise<IPodSetupState> => {
    const silent = { validateStatus: () => true };
    const [accessRes, readinessRes, typesRes] = await Promise.all([
      axiosInstance.get<IScheduleAssistAccess>('/SchedulingPod/access', silent),
      axiosInstance.get<ICalendarReadiness>('/SchedulingPod/readiness', silent),
      fetchAppointmentTypesPaged(silent),
    ]);
    const ok = (status: number): boolean => status >= 200 && status < 300;
    const access = ok(accessRes.status) ? accessRes.data : null;
    const moduleEnabled = access
      ? access.cause !== ACCESS_CAUSE.moduleDisabled
      : readinessRes.status !== 403 && typesRes.status !== 403;
    return {
      moduleEnabled,
      access,
      readiness: ok(readinessRes.status) ? readinessRes.data : null,
      appointmentTypes: ok(typesRes.status) && Array.isArray(typesRes.data) ? typesRes.data : null,
    };
  },

  getCalendarHealth: async (
    appointmentTypeId: number,
    techUserIds: number[],
    assignmentStrategy?: number,
  ): Promise<ICalendarHealthCheck | null> => {
    try {
      const res = await axiosInstance.request<unknown>(
        podRequest({
          method: 'GET',
          url: '/SchedulingPod/calendar-health',
          params: {
            appointmentTypeId,
            techUserIds: techUserIds.length > 0 ? techUserIds.join(',') : undefined,
            assignmentStrategy: techUserIds.length > 0 ? assignmentStrategy : undefined,
          },
          validateStatus: () => true,
          skipErrorSnackbar: true,
          timeout: 10000,
        }),
      );
      if (!res || res.status < 200 || res.status >= 300) return null;
      return parseCalendarHealth(res.data);
    } catch {
      return null;
    }
  },

  /** Returns calendar readiness for the current Pod user (pre-checks a booking). */
  getReadiness: () =>
    axiosInstance.request<ICalendarReadiness>({
      method: 'GET',
      url: '/SchedulingPod/readiness',
      validateStatus: () => true,
    }),

  getZoomAvailable: async (): Promise<boolean> => {
    try {
      const res = await axiosInstance.request<{ available?: boolean }>(
        podRequest({
          method: 'GET',
          url: '/ScheduleAssist/CalendarConnections/zoom/available',
          validateStatus: () => true,
          skipErrorSnackbar: true,
        }),
      );
      return !!res && res.status >= 200 && res.status < 300 && res.data?.available === true;
    } catch {
      return false;
    }
  },

  /** Fetches intersected availability for multiple techs. */
  getAvailabilityForTechs: (
    appointmentTypeSlug: string,
    techUserIds: number[],
    startDate: string,
    endDate: string,
    durationMinutes: number,
    override?: { assignmentStrategy: number; distributionMode?: number } | null,
  ) =>
    axiosInstance.request<IAvailableSlot[]>({
      method: 'GET',
      url: '/SchedulingPod/availability',
      params: {
        appointmentTypeSlug,
        techUserIds: techUserIds.join(','),
        assignmentStrategy: override?.assignmentStrategy,
        distributionMode: override?.distributionMode,
        startDate,
        endDate,
        durationMinutes,
      },
    }),
};

export default scheduleAssistService;
